import { useQuery } from '@tanstack/react-query';
import type { LineStringGeometry, PointGeometry, PolygonGeometry } from '@trafiku/contracts';
import type { Feature, FeatureCollection } from 'geojson';
import { fc } from '@/components/map/layers';
import { api } from './api';
import { safeHex } from './colors';

export interface Network {
  stations: Array<{ _id: string; name: string; geometry: PointGeometry; lines: string[] }>;
  lines: Array<{
    _id: string;
    name: string;
    geometry: LineStringGeometry;
    ordered_stations: string[];
    color?: string;
    bus_sign?: string;
  }>;
  zones: Array<{
    _id: string;
    name: string;
    geometry_going: PolygonGeometry;
    geometry_return: PolygonGeometry;
    on_line_going?: string;
    on_line_return?: string;
    color: string;
    bus_sign: string;
    is_active: boolean;
  }>;
  out_of_service_areas: Array<{ _id: string; name: string; geometry: PolygonGeometry }>;
}

/** Stations, lines, zones and areas for map context. */
export function useNetwork() {
  return useQuery({ queryKey: ['network'], queryFn: () => api<Network>('/network'), staleTime: 30_000 });
}

export function linesCollection(
  lines: Network['lines'] = [],
  only?: (id: string) => boolean,
): FeatureCollection {
  return fc(
    lines
      .filter((l) => (only ? only(l._id) : true))
      .map(
        (l): Feature => ({
          type: 'Feature',
          geometry: l.geometry,
          properties: { id: l._id, name: l.name, color: safeHex(l.color) },
        }),
      ),
  );
}

export function stationsCollection(stations: Network['stations'] = [], exclude?: string): FeatureCollection {
  return fc(
    stations
      .filter((s) => s._id !== exclude)
      .map(
        (s): Feature => ({
          type: 'Feature',
          geometry: s.geometry,
          properties: {
            id: s._id,
            name: s.name,
            lng: s.geometry.coordinates[0],
            lat: s.geometry.coordinates[1],
          },
        }),
      ),
  );
}

/** Each zone as its two lines (going and return) in the zone colour; id = zone id. */
export function zonesCollection(network?: Network): FeatureCollection {
  const lines = new Map(network?.lines.map((l) => [l._id, l]));
  return fc(
    (network?.zones ?? []).flatMap((z) =>
      [z.on_line_going, z.on_line_return]
        .map((id) => (id ? lines.get(id) : undefined))
        .filter((l) => !!l)
        .map(
          (l): Feature => ({
            type: 'Feature',
            geometry: l.geometry,
            properties: { id: z._id, name: z.name, color: safeHex(z.color) },
          }),
        ),
    ),
  );
}

/** Terminus fences of all zones (context under the zone lines). */
export function zoneFencesCollection(network?: Network): FeatureCollection {
  return fc(
    (network?.zones ?? []).flatMap((z) =>
      [z.geometry_going, z.geometry_return]
        .filter((g) => !!g)
        .map(
          (g): Feature => ({
            type: 'Feature',
            geometry: g,
            properties: { id: z._id, color: safeHex(z.color) },
          }),
        ),
    ),
  );
}

/** Any items with a point position, for overview maps. */
export function pointsCollection<T extends { _id: string }>(
  items: T[] = [],
  position: (item: T) => [number, number] | undefined,
  name: (item: T) => string,
  color: string,
): FeatureCollection {
  return fc(
    items.flatMap((item): Feature[] => {
      const p = position(item);
      return p && Number.isFinite(p[0]) && Number.isFinite(p[1])
        ? [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: p },
              properties: { id: item._id, name: name(item), color },
            },
          ]
        : [];
    }),
  );
}

/** Lines as search options, sorted by name (numbers in natural order). */
export function lineOptions(lines: Array<{ _id: string; name: string; bus_sign?: string }> = []) {
  return [...lines]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((l) => ({ value: l._id, label: l.name, hint: l.bus_sign ? `#${l.bus_sign}` : undefined }));
}
